import { cn } from '@/lib/utils'
import { ChevronDown, Globe } from 'lucide-react'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@teach/ui'
import type { LangFilter } from '@/types/lang'
import { LanguageFlag } from '@/components/layout/sidebar/LanguageFlag'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: LangFilter
  onChange: (next: LangFilter) => void
  className?: string
}

/** Значок варианта: флаг в кружке, у «Все языки» — глобус. */
const Mark = ({ lang, gap = 'mr-2' }: { lang: LangFilter; gap?: string }) =>
  lang === 'all' ? (
    <Globe className={cn(gap, 'size-5 text-muted-foreground')} />
  ) : (
    <LanguageFlag lang={lang} className={gap} />
  )

/**
 * Язык курсов в Explore: все, русские или английские. Та же подача, что у
 * выбора языка в меню профиля (`LanguageMenu`): флаги в кружках, радио-пункты.
 * `all` сильнее профиля на бэке.
 */
export const LanguageFilterDropdown = ({ value, onChange, className }: Props) => {
  const m = useMessages(messages)
  const label = value === 'ru' ? 'Русский' : value === 'en' ? 'English' : m.allLanguages
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Уже `lg` — квадратная кнопка с одним флагом: поиску отдаём ширину. */}
        <Button
          variant="outline"
          aria-label={label}
          className={cn('w-14 shrink-0 px-0 lg:w-auto lg:px-4', className)}
        >
          <Mark lang={value} gap="lg:mr-2" />
          <span className="hidden lg:inline">{label}</span>
          <ChevronDown className="ml-2 hidden size-4 lg:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 [&>div>*+*]:mt-0.5">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(v) => onChange(v === 'ru' || v === 'en' ? v : 'all')}
        >
          <DropdownMenuRadioItem value="all">
            <Mark lang="all" />
            {m.allLanguages}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="ru">
            <Mark lang="ru" />
            Русский
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="en">
            <Mark lang="en" />
            English
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
